export interface LogEntry {
  id: string;
  time: string;
  level: 'WARN' | 'INFO' | 'ERROR';
  component: string;
  message: string;
  tsNs: string;
}

// Helper function to generate random hex string
const generateRandomHex = (length: number): string => {
  const chars = '0123456789abcdef';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars[Math.floor(Math.random() * chars.length)];
  }
  return result;
};

// Helper function to generate random timestamp
const generateTimestamp = (baseTime: string, offsetMinutes: number): string => {
  const baseDate = new Date(baseTime);
  const newDate = new Date(baseDate.getTime() + (offsetMinutes * 60000));
  return newDate.toISOString().slice(0, 19).replace('T', ' ');
};

// Helper function to vary numeric values
const varyNumber = (base: string, variation: number = 1000000): string => {
  const baseNum = parseInt(base);
  const varied = baseNum + Math.floor(Math.random() * variation * 2) - variation;
  return varied.toString();
};

// Base log entries
const baseEntries: LogEntry[] = [
  {
    id: "1756814647014245323_a1329238",
    time: "2025-09-02 14:04:07",
    level: "WARN",
    component: "c.c.a.u.DofUtils$",
    message: "Missed to enrich exclusion details for: GzipInvalidPayload(java.lang.IllegalArgumentException: Illegal base64 character 20)",
    tsNs: "1756814647014245323"
  },
  {
    id: "1756814642159324000_423ec0fe",
    time: "2025-09-02 14:04:02",
    level: "WARN",
    component: "c.c.a.u.DofUtils$",
    message: "Missed to enrich exclusion details for: GzipInvalidPayload(java.lang.IllegalArgumentException: Illegal base64 character 20)",
    tsNs: "1756814642159324000"
  },
  {
    id: "1756814581836642060_cc21cda9",
    time: "2025-09-02 14:03:01",
    level: "WARN",
    component: "c.c.a.u.DofUtils$",
    message: "Missed to enrich exclusion details for: GzipInvalidPayload(java.lang.IllegalArgumentException: Illegal base64 character 20)",
    tsNs: "1756814581836642060"
  },
  {
    id: "1756814547689925888_ea901952",
    time: "2025-09-02 14:02:27",
    level: "INFO",
    component: "c.c.t.Refresher",
    message: "TenantContext updated (schemaVersion=1)",
    tsNs: "1756814547689925888"
  },
  {
    id: "1756814283937139975_1a8c4574",
    time: "2025-09-02 13:58:03",
    level: "INFO",
    component: "o.a.k.c.c.i.ConsumerUtils",
    message: "Setting offset for partition lr1-get-124 to the committed offset FetchPosition{offset=11172304219, offsetEpoch=Optional.empty, currentLeader=LeaderAndEpoch{leader=Optional[kok-transformed-kafka-0.kok-transformed-kafka-brokers.kok-transformed.svc:9092 (id: 0 rack: null)], epoch=96}}",
    tsNs: "1756814283937139975"
  },
  {
    id: "1756814253641479758_66385e0d",
    time: "2025-09-02 13:57:33",
    level: "INFO",
    component: "o.a.k.c.c.i.ConsumerCoordinator",
    message: "(Re-)joining group",
    tsNs: "1756814253641479758"
  },
  {
    id: "1756814240828015862_e7453f25",
    time: "2025-09-02 13:57:20",
    level: "WARN",
    component: "c.c.a.u.DofUtils$",
    message: "Missed to enrich exclusion details for: GzipInvalidPayload(java.lang.IllegalArgumentException: Illegal base64 character 20)",
    tsNs: "1756814240828015862"
  },
  {
    id: "1756814160719416984_3c625b61",
    time: "2025-09-02 13:56:00",
    level: "WARN",
    component: "c.c.a.u.DofUtils$",
    message: "Missed to enrich exclusion details for: GzipInvalidPayload(java.lang.IllegalArgumentException: Illegal base64 character 25)",
    tsNs: "1756814160719416984"
  },
  {
    id: "1756814106453886645_e1426d37",
    time: "2025-09-02 13:55:06",
    level: "ERROR",
    component: "c.c.a.u.DofUtils$",
    message: "Failed to process payload: OutOfMemoryError",
    tsNs: "1756814106453886645"
  },
  {
    id: "1756814061940862875_427afd8a",
    time: "2025-09-02 13:54:21",
    level: "INFO",
    component: "c.c.s.ServiceMonitor",
    message: "Health check passed for all services",
    tsNs: "1756814061940862875"
  }
];

// Additional message templates for variety
const messageTemplates = [
  "Connection timeout to downstream service after 30s",
  "Database connection pool exhausted, waiting for available connection",
  "Cache miss for key: user_session_{id}, fetching from database",
  "Rate limit exceeded for client IP: {ip}, request rejected",
  "Message processing completed successfully in {time}ms",
  "Partition rebalance triggered for consumer group: {group}",
  "SSL certificate validation failed for endpoint: {endpoint}",
  "Memory usage warning: {percent}% of heap space used",
  "Failed to serialize object of type: {type}",
  "Async task completed with status: {status}",
  "Network latency spike detected: {latency}ms average",
  "Authentication failed for user: {user}",
  "Backup operation completed successfully",
  "Dead letter queue processing started",
  "Circuit breaker opened for service: {service}"
];

const components = [
  "c.c.a.u.DofUtils$",
  "o.a.k.c.c.i.ConsumerUtils",
  "o.a.k.c.c.i.ConsumerCoordinator", 
  "c.c.t.Refresher",
  "c.c.s.ServiceMonitor",
  "c.c.d.DatabaseManager",
  "c.c.n.NetworkService",
  "c.c.a.AuthenticationService",
  "c.c.c.CacheManager",
  "c.c.m.MessageProcessor"
];

const levels: ('WARN' | 'INFO' | 'ERROR')[] = ['WARN', 'INFO', 'ERROR'];

// Generate 750 log entries (25x the original ~30)
export const logsData: LogEntry[] = [];

for (let i = 0; i < 750; i++) {
  const baseEntry = baseEntries[i % baseEntries.length];
  const minutesOffset = Math.floor(Math.random() * 1440) - 720; // Random within ±12 hours
  
  // Vary the message
  let message;
  if (i % 5 === 0 && messageTemplates.length > 0) {
    message = messageTemplates[Math.floor(Math.random() * messageTemplates.length)]
      .replace('{id}', generateRandomHex(8))
      .replace('{ip}', `192.168.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`)
      .replace('{time}', (Math.random() * 5000 + 100).toFixed(0))
      .replace('{group}', `consumer-group-${Math.floor(Math.random() * 10)}`)
      .replace('{endpoint}', `https://api-${Math.floor(Math.random() * 5)}.example.com`)
      .replace('{percent}', (Math.random() * 40 + 60).toFixed(1))
      .replace('{type}', `com.example.Type${Math.floor(Math.random() * 20)}`)
      .replace('{status}', ['SUCCESS', 'FAILED', 'PARTIAL'][Math.floor(Math.random() * 3)])
      .replace('{latency}', (Math.random() * 2000 + 50).toFixed(0))
      .replace('{user}', `user_${generateRandomHex(6)}`)
      .replace('{service}', `service-${Math.floor(Math.random() * 8)}`);
  } else {
    message = baseEntry.message;
  }
  
  const newEntry: LogEntry = {
    id: `${varyNumber(baseEntry.tsNs)}_${generateRandomHex(8)}`,
    time: generateTimestamp(baseEntry.time, minutesOffset),
    level: i % 7 === 0 ? levels[Math.floor(Math.random() * levels.length)] : baseEntry.level,
    component: i % 4 === 0 ? components[Math.floor(Math.random() * components.length)] : baseEntry.component,
    message: message,
    tsNs: varyNumber(baseEntry.tsNs)
  };
  
  logsData.push(newEntry);
}

// Sort by timestamp descending (newest first)
logsData.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
